import React from 'react'
import { Button, Tooltip } from 'antd'
import styled from 'styled-components/macro'
import SuccessHubBadge from 'src/components/growthEngine/SuccessHubBadge'
import { formatGuidedEmptyMinutes } from 'src/utils/growthEngineRecommendation'

const MISSION_MARK_SIZE = 20

/**
 * Quiet secondary leftover under today’s job — olive link CTA, not a second
 * lime primary. Soft mark vs the job badge. Same picker / adoption path.
 */
const SuccessHubMissionCard = ({
  mission,
  ctaLabel,
  ctaAction,
  honestNote,
  onCta,
  className,
  'data-testid': dataTestId,
}) => {
  if (!mission?.intentId) return null
  const testId = dataTestId || `progress-mission-${mission.intentId}`
  const minutesLabel = formatGuidedEmptyMinutes(mission.estimatedMinutes)
  const benefit = String(mission.whyItMatters || mission.body || '').trim()
  const showCta = Boolean(ctaLabel && ctaAction && onCta)
  const tip =
    benefit ||
    'A leftover from the same “what next” engine as today’s kitchen job.'

  return (
    <Tooltip title={tip} mouseEnterDelay={0.25} placement='topLeft'>
      <Card className={className} data-testid={testId}>
        {mission.badge ? (
          <QuietMark data-testid={`${testId}-mark-wrap`}>
            <SuccessHubBadge
              badge={mission.badge}
              title={mission.title}
              size={MISSION_MARK_SIZE}
              data-testid={`${testId}-mark`}
            />
          </QuietMark>
        ) : null}
        <Copy>
          {mission.title ? (
            <Title data-testid={`${testId}-title`}>{mission.title}</Title>
          ) : null}
          {benefit ? (
            <Benefit data-testid={`${testId}-benefit`}>{benefit}</Benefit>
          ) : null}
          {minutesLabel ? (
            <Minutes data-testid={`${testId}-minutes`}>{minutesLabel}</Minutes>
          ) : null}
          {honestNote ? (
            <HonestNote data-testid='progress-mission-stock-honest'>
              {honestNote}
            </HonestNote>
          ) : null}
        </Copy>
        {showCta ? (
          <Cta
            type='link'
            onClick={onCta}
            data-testid={`${testId}-cta`}
            data-chrome='quiet'
          >
            {ctaLabel}
          </Cta>
        ) : null}
      </Card>
    </Tooltip>
  )
}

export default SuccessHubMissionCard

const Card = styled.li`
  align-items: flex-start;
  background: transparent;
  border: none;
  border-bottom: 1px solid
    ${({ theme }) => theme?.colours?.chipGrey || 'rgba(0, 0, 0, 0.06)'};
  border-radius: 0;
  cursor: help;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  list-style: none;
  padding: 10px 0;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`

const QuietMark = styled.div`
  flex-shrink: 0;
  filter: saturate(0.45);
  opacity: 0.72;
`

const Copy = styled.div`
  display: flex;
  flex: 1 1 12rem;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Title = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Benefit = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: 12px;
  line-height: 1.45;
  margin: 0;
`

const Minutes = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const HonestNote = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const Cta = styled(Button)`
  && {
    align-self: center;
    color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
    font-size: 13px;
    font-weight: 600;
    height: auto;
    margin-left: auto;
    padding: 0;
  }

  &&:hover,
  &&:focus {
    color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  }
`
